import {
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Icon from 'components/Icon';
import { EMPTY_ARRAY } from 'constants';
import useFetchedData from 'hooks/useFetchedData';
import React, { useEffect, useState } from 'react';

function CompanyNameInput({
  onChange, onSelectingLocation, value, ...props
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(value?.name || '');
  const { data: companies } = useFetchedData(
    value.name && {
      url: 'v1/companies/',
      query: {
        search: value.name,
        page: 1,
      },
    },
    ({ data } = {}) => data?.results || EMPTY_ARRAY,
  );
  const active = open && companies.length > 0;

  const options = companies;

  useEffect(() => {
    const timer = setTimeout(() => {
      onChange({ name: searchQuery, id: companies.find((c) => c.name === searchQuery)?.id });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, companies]);

  function getEndAdornment() {
    if (searchQuery) {
      return (
        <Icon
          sx={{
            cursor: 'pointer', border: 'none', bgcolor: 'transparent', p: 0,
          }}
          component="button"
          type="button"
          size={20}
          name="close-filled"
          color="text.disabled"
          onClick={() => {
            onChange({ name: '', id: null });
            setSearchQuery('');
          }}
        />
      );
    }
    return null;
  }

  async function handleSelect(_, selectedOption) {
    if (!selectedOption || typeof selectedOption !== 'object') {
      return;
    }
    const { id, name } = selectedOption;
    setSearchQuery(name);
    document.activeElement?.blur();
    onChange({ id, name });
  }

  const highlightText = (text, query) => {
    if (!query) return text;
    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return (
      <span>
        {parts.filter(Boolean).map((part, index) => (
          (
            <Typography
              key={index}
              component="span"
              sx={{ fontWeight: 'bold' }}
              variant="label-md"
              color={part.toLowerCase() === query.toLowerCase() ? 'text.primary' : 'text.secondary'}
            >
              {part}
            </Typography>
          )
        ))}
      </span>
    );
  };

  return (
    <Autocomplete
      freeSolo
      open={active}
      clearIcon={null}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      componentsProps={{
        paper: {
          elevation: 0,
          sx: (theme) => ({
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            mt: 0,
            p: 0,
            mx: '-1px',
            border: `1px solid ${theme.palette.border['100%']}`,
            borderTop: 'none',
            bgcolor: '#fff',
            maxHeight: '160px',
            '& .MuiAutocomplete-listbox': {
              p: 0,
              '& .MuiAutocomplete-option': {
                p: 2,
                borderBottom: `1px solid ${theme.palette.border.default}`,
                ...theme.typography['body-md'],
                '&:last-child': {
                  borderBottom: 'none',
                },
                '& .Mui-focused, &:hover': {
                  bgcolor: theme.palette.background.elevation2,
                },
              },
            },
          }),
        },
      }}
      getOptionLabel={(option) => option.description || searchQuery}
      filterOptions={(o) => o}
      options={options}
      onChange={handleSelect}
      inputValue={searchQuery}
      renderInput={({ InputProps, ...inputProps }) => (
        <TextField
          onChange={({ target: { value } }) => setSearchQuery(value)}
          placeholder="Company name"
          variant="filled"
          size="small"
          sx={(theme) => ({
            '& .MuiInputBase-root': {
              px: 2,
              py: 1.5,
              pr: '16px !important',
              border: `1px solid ${theme.palette.border['100%']}`,
              bgcolor: '#fff',
              '&::before,&::after': {
                display: 'none',
              },
              '& .MuiInputBase-input': {
                p: 0,
              },
              borderRadius: 3,
              ...(active ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
                borderBottom: 'none',
              } : {}),
            },
          })}
          InputProps={{
            ...InputProps,
            endAdornment: (
              <InputAdornment position="end">
                {getEndAdornment()}
              </InputAdornment>
            ),
          }}
          {...inputProps}
        />
      )}
      forcePopupIcon={false}
      renderOption={(props, option) => (
        <li {...props}>
          {highlightText(option.name, searchQuery)}
        </li>
      )}
      {...props}
    />
  );
}

export default CompanyNameInput;
